/**
 * Hook to submit feedback on a moderation case
 */

import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import type { SubmitCaseFeedbackMutation } from '@core/graphql/types/operations';
import { useState } from 'react';

import type { CaseFeedbackInput, CaseFeedbackResponse } from '../domain/CaseFeedback';
import { GET_TOPIC_MODERATION_STATS, SUBMIT_CASE_FEEDBACK } from '../graphql';

/**
 * Hook to submit moderation case feedback
 * @returns Mutation function to submit feedback, loading state, errors, and reset function
 */
export function useSubmitCaseFeedback() {
  const [submitMutation, { loading }] = useMutation<SubmitCaseFeedbackMutation>(SUBMIT_CASE_FEEDBACK);
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const submitFeedback = async (input: CaseFeedbackInput): Promise<CaseFeedbackResponse | null> => {
    try {
      setErrors(null);
      const { data } = await submitMutation({
        variables: { input },
        refetchQueries: [
          {
            query: GET_TOPIC_MODERATION_STATS,
            variables: {
              communityId: input.communityId,
              topicName: input.topicName,
            },
          },
        ],
      });

      return data?.submitCaseFeedback || null;
    } catch (error) {
      setErrors(transformErrors(error));

      return null;
    }
  };

  const resetErrors = () => setErrors(null);

  return {
    submitFeedback,
    loading,
    errors,
    resetErrors,
  };
}
